import { QUERY_KEYS } from '@/remote/query-key';
import type { ApiResponse } from '@/types/site';
import { useInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { XHRAuthorizedClient } from '../client';
import { ENDPOINTS } from '../endpoints';

// 어드민 사이트 로그 쿼리 파라미터 타입
export interface AdminSiteLogQuery {
  page?: number;
  limit?: number;
}

// 어드민 사이트 로그 개별 데이터 타입
export interface AdminSiteLogData {
  siteId: string;
  cdmaNumber: string;
  waterLevel: number;
  lowWaterLevel: number;
  highWaterLevel: number;
  inflow: number;
  chemicalLevel: number;
  lowChemicalLevel: number;
  highChemicalLevel: number;
  errorCode: string;
  timestamp: number;
  timestampString: string;
  temperature: number;
  motorRun: boolean;
  motor1Status: boolean | null;
  motor2Status: boolean | null;
  motor3Status: boolean | null;
  circuitBreakerError: boolean | null;
  externalError: boolean | null;
  pressureSensorError: boolean | null;
  farmingMode: boolean;
  cycleMode: boolean;
  hasNoError: boolean;
  preparation: boolean;
  preparation2: boolean;
  rawPacketData: string | null;
}

// 페이지네이션 정보 타입
export interface AdminSiteLogPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

// 어드민 사이트 로그 응답 타입
export interface AdminSiteLogResponse {
  logs: AdminSiteLogData[];
  pagination: AdminSiteLogPagination;
}

// 어드민 사이트 로그 API 응답 타입
export type AdminSiteLogApiResponse = ApiResponse<AdminSiteLogResponse>;

// 어드민 사이트 로그 조회 훅
export const useAdminSiteLogsQuery = (query: AdminSiteLogQuery = {}) => {
  const page = query.page || 1;
  const limit = query.limit || 50;

  return useSuspenseQuery({
    queryKey: QUERY_KEYS.sites.getAdminLogs(page, limit),
    queryFn: async (): Promise<AdminSiteLogResponse> => {
      const params = new URLSearchParams();
      if (page) params.append('page', page.toString());
      if (limit) params.append('limit', limit.toString());

      const queryString = params.toString();
      const url = queryString
        ? ENDPOINTS.sites.adminLogs + `?${queryString}`
        : ENDPOINTS.sites.adminLogs;

      const response =
        await XHRAuthorizedClient.get<AdminSiteLogApiResponse>(url);
      return response.data.data;
    },
  });
};

// 무한 스크롤을 위한 어드민 사이트 로그 조회 훅
export const useInfiniteAdminSiteLogsQuery = (limit: number = 50) => {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.sites.getAdminLogs(limit, 50),
    queryFn: async ({ pageParam = 1 }): Promise<AdminSiteLogResponse> => {
      const params = new URLSearchParams();
      params.append('page', pageParam.toString());
      params.append('limit', limit.toString());

      const url = ENDPOINTS.sites.adminLogs + `?${params.toString()}`;

      const response =
        await XHRAuthorizedClient.get<AdminSiteLogApiResponse>(url);
      return response.data.data;
    },
    getNextPageParam: (lastPage) => {
      // 다음 페이지가 있는지 확인
      if (lastPage.pagination.currentPage < lastPage.pagination.totalPages) {
        return lastPage.pagination.currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });
};
